import { useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Trash2 } from 'lucide-react';
import styled from 'styled-components';

import {
    useDeletePriceInvisibilityRuleMutation,
    useGetPriceInvisibilityRuleQuery,
    useUpdatePriceInvisibilityRuleMutation,
    TotadminPermission,
} from 'data/__generated__';
import { organizationSource, productSource } from 'data/entity-sources/sources';

import { PAGES } from 'constants/pages';

import { confirmPopup } from 'components/Popups/Confirm';
import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';
import { SectionContainer } from 'components/DetailsView/Section/SectionContainer';
import { TotemInput } from 'components/TotemInput';
import { UltraSelect } from 'components/UltraSelect';

type ParamTypes = {
    priceInvisibilityRuleId: string;
};

export const PriceInvisibilityRuleDetails = () => {
    const { priceInvisibilityRuleId = '' } = useParams<ParamTypes>();
    const navigate = useNavigate();

    const { loading, data, error } = useGetPriceInvisibilityRuleQuery({
        variables: { _id: priceInvisibilityRuleId },
    });
    const [updateRule, { loading: updateLoading }] = useUpdatePriceInvisibilityRuleMutation();
    const [deleteRule, { loading: deleteLoading }] = useDeletePriceInvisibilityRuleMutation({
        refetchQueries: ['GET_PRICE_INVISIBILITY_RULES'],
    });

    const [name, setName] = useState<string>('');
    const [organizationIds, setOrganizationIds] = useState<string[]>([]);
    const [productIds, setProductIds] = useState<string[]>([]);

    const rule = data?.priceInvisibilityRule;

    useEffect(() => {
        if (rule) {
            setName(rule.name);
            setOrganizationIds(rule.organizationIds);
            setProductIds(rule.productIds);
        }
    }, [rule]);

    if (loading) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <Loader />
            </div>
        );
    }

    if (!rule || error) {
        throw new Error("Une erreur est survenue lors de la récupération de la règle d'invisibilité");
    }

    const handleSave = async () => {
        try {
            await updateRule({
                variables: { _id: priceInvisibilityRuleId, input: { name, organizationIds, productIds } },
            });
            toast.success('Règle mise à jour');
        } catch (mutationError) {
            toast.error(mutationError instanceof Error ? mutationError.message : 'Une erreur est survenue');
        }
    };

    const handleDelete = async () => {
        const confirmed = await confirmPopup({
            title: 'Supprimer la règle',
            message: `Les prix des ${rule.productIds.length} produit(s) redeviendront visibles pour ${rule.organizationIds.length} organisation(s).`,
            isDestructive: true,
        });
        if (!confirmed) return;

        try {
            await deleteRule({ variables: { _id: priceInvisibilityRuleId } });
            toast.success('Règle supprimée');
            navigate('/price-invisibility-rules');
        } catch (mutationError) {
            toast.error(mutationError instanceof Error ? mutationError.message : 'Une erreur est survenue');
        }
    };

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/price-invisibility-rules' }],
        },
        {
            label: 'Actions',
            items: [
                {
                    label: 'Supprimer la règle',
                    icon: <Trash2 />,
                    onClick: handleDelete,
                    requiredPermissions: [TotadminPermission.PriceInvisibilityRulesManage],
                },
            ],
        },
    ];

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.priceInvisibilityRuleDetails} />
                </HeaderTitle>
                <ActionsAndSave
                    actionGroups={actionGroups}
                    onSave={handleSave}
                    saveLoading={updateLoading || deleteLoading}
                    saveDisabled={!name || !organizationIds.length || !productIds.length}
                    saveRequiredPermissions={[TotadminPermission.PriceInvisibilityRulesManage]}
                />
            </Header>
            <Content>
                <SectionContainer title="Règle" isInitiallyOpen>
                    <Fields>
                        <TotemInput
                            label="Nom de la règle"
                            sublabel="Pourquoi ces prix sont masqués"
                            onChange={setName}
                            value={name}
                        />
                        <UltraSelect
                            source={organizationSource}
                            label="Organisations"
                            sublabel="Clients pour qui ces prix sont masqués"
                            multiple
                            value={organizationIds}
                            onChange={setOrganizationIds}
                        />
                        <UltraSelect
                            source={productSource}
                            label="Produits"
                            sublabel="Produits dont le prix n'est pas affiché sur les webapps"
                            multiple
                            value={productIds}
                            onChange={setProductIds}
                        />
                    </Fields>
                </SectionContainer>
                <p className="text-muted-foreground mx-[5px] text-[14px]">
                    Le masquage est purement un affichage : le prix du produit, le total de la commande et la
                    facturation ne changent pas.
                </p>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    overflow-y: auto;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    padding: 15px;
    gap: 10px;
`;

const Fields = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding: 10px 0;
`;
